import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { EyeInvisibleOutlined, EyeOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons'
import { AuthError, useAuth } from '../auth'
import { setLanguage } from '../i18n'
import { useTheme } from '../theme'
import AuthShell, { AuthField } from '../components/AuthShell'

/**
 * Turn the identity provider's error into something a person can act on.
 *
 * Keycloak answers a throttled account with the same "invalid user credentials"
 * as a wrong password, on purpose, so that an attacker cannot use the response
 * to discover which usernames exist. That means the lockout can only be
 * inferred, so after a few failures in a row we say so rather than let someone
 * retype a password that is actually correct.
 */
function explain(e: unknown, failures: number, t: (k: string) => string): string {
  if (e instanceof AuthError) {
    const d = e.message.toLowerCase()
    if (d.includes('not fully set up')) return t('errIncomplete')
    if (d.includes('temporarily disabled') || d.includes('too many')) return t('errLocked')
    if (d.includes('disabled')) return t('errDisabled')
    if (e.code === 'invalid_grant') return failures >= 3 ? t('errMaybeLocked') : t('errCredentials')
    return e.message
  }
  return t('errUnreachable')
}

export default function Login({ onRegister, onForgot }: { onRegister?: () => void; onForgot?: () => void }) {
  const { t, i18n } = useTranslation()
  const { signIn } = useAuth()
  const { resolved, setMode } = useTheme()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [failures, setFailures] = useState(0)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password || busy) return
    // A Mongolian phone number is the natural thing to type, and Keycloak has no
    // concept of one — it would answer "invalid user credentials", which reads
    // as a wrong password rather than the wrong kind of identifier.
    if (/^[0-9\s+-]{8,}$/.test(username.trim())) {
      setError(t('errPhoneLogin'))
      return
    }
    setBusy(true)
    setError('')
    try {
      await signIn(username.trim(), password)
    } catch (err) {
      const n = failures + 1
      setFailures(n)
      setError(explain(err, n, t))
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  const lang = i18n.language.startsWith('mn') ? 'mn' : 'en'

  return (
    <AuthShell
      as="form" onSubmit={submit}
      title={t('signIn')}
      lede={t('signInHint')}
      foot={
        <>
          <div className="row" style={{ gap: 4 }}>
            <button type="button" className="chip" onClick={() => setLanguage(lang === 'mn' ? 'en' : 'mn')}>
              {lang === 'mn' ? 'EN' : 'MN'}
            </button>
            <button type="button" className="chip" aria-label={t('theme')}
              onClick={() => setMode(resolved === 'dark' ? 'light' : 'dark')}>
              {resolved === 'dark' ? '☀' : '☾'}
            </button>
          </div>
          {onRegister && (
            <span className="auth-alt">
              {t('noAccountYet')}{' '}
              <button type="button" className="linkish" onClick={onRegister}>{t('createAccount')}</button>
            </span>
          )}
        </>
      }
    >
      {error && (
        <div className="auth-error" role="alert">
          <WarningOutlined aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <AuthField
        id="u" label={t('username')} value={username} onChange={setUsername}
        name="username" autoComplete="username" autoCapitalize="none"
        autoCorrect="off" spellCheck={false} autoFocus disabled={busy}
      />

      <AuthField
        id="p" label={t('password')} value={password} onChange={setPassword}
        name="password" type={show ? 'text' : 'password'}
        autoComplete="current-password" disabled={busy}
      >
        <button type="button" className="icon-btn" onClick={() => setShow(v => !v)}
          aria-label={show ? t('hidePassword') : t('showPassword')}>
          {show ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        </button>
      </AuthField>

      {onForgot && (
        <div className="auth-row-end">
          <button type="button" className="linkish" onClick={onForgot}>{t('forgotPassword')}</button>
        </div>
      )}

      <button className="auth-submit" type="submit" disabled={busy || !username.trim() || !password}>
        {busy ? <><LoadingOutlined /> {t('signingIn')}</> : t('signIn')}
      </button>

      {import.meta.env.DEV && (
        <p className="auth-hint">
          {t('devUsers')}: <code>alice / alice</code> · <code>bob / bob</code> · <code>operator / operator</code>
        </p>
      )}
    </AuthShell>
  )
}
